"use client"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useEffect, useRef, useState } from "react"
import { useT } from "@lib/i18n/use-t"

function CircuitCanvas() {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const c = ref.current
    if (!c) return
    const ctx = c.getContext("2d")
    if (!ctx) return

    const isDark = document.documentElement.classList.contains("dark")
    const nodeColor = isDark ? "rgba(0,212,255,0.15)" : "rgba(0,119,190,0.10)"
    const lineColor = isDark ? "rgba(0,212,255,0.06)" : "rgba(0,119,190,0.04)"

    const dpr = window.devicePixelRatio || 1
    const resize = () => {
      c.width = c.offsetWidth * dpr
      c.height = c.offsetHeight * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()

    const nodes = Array.from({ length: 45 }, () => ({
      x: Math.random() * c.offsetWidth,
      y: Math.random() * c.offsetHeight,
      vx: (Math.random() - 0.5) * 0.22,
      vy: (Math.random() - 0.5) * 0.22,
    }))

    let raf: number
    const draw = () => {
      const W = c.offsetWidth
      const H = c.offsetHeight
      ctx.clearRect(0, 0, W, H)
      nodes.forEach((n) => {
        n.x += n.vx
        n.y += n.vy
        if (n.x < 0 || n.x > W) n.vx *= -1
        if (n.y < 0 || n.y > H) n.vy *= -1
      })
      ctx.strokeStyle = lineColor
      ctx.lineWidth = 1
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y)
          if (d < 120) {
            ctx.beginPath()
            ctx.moveTo(nodes[i].x, nodes[i].y)
            ctx.lineTo(nodes[j].x, nodes[j].y)
            ctx.stroke()
          }
        }
      }
      ctx.fillStyle = nodeColor
      nodes.forEach((n) => {
        ctx.beginPath()
        ctx.arc(n.x, n.y, 1.8, 0, Math.PI * 2)
        ctx.fill()
      })
      raf = requestAnimationFrame(draw)
    }
    draw()
    window.addEventListener("resize", resize)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener("resize", resize)
    }
  }, [])

  return (
    <canvas
      ref={ref}
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  )
}

export default function Hero() {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const t = useT()

  const STATS = [
    { value: "16 000+", label: t("heroStatProducts") },
    { value: "< 2ms", label: t("heroStatSpeed") },
    { value: "152", label: t("heroStatCategories") },
    { value: "24/7", label: t("heroStatSupport") },
  ]

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-slate-100 via-blue-50/50 to-slate-100 dark:from-[#0a0e1a] dark:via-[#0f172a] dark:to-[#0c1425]">
      <CircuitCanvas />

      {/* Glow orbs */}
      <div className="pointer-events-none absolute -right-[10%] -top-[20%] h-[600px] w-[600px] rounded-full bg-[radial-gradient(circle,rgba(0,119,190,0.04)_0%,transparent_70%)] dark:bg-[radial-gradient(circle,rgba(0,212,255,0.03)_0%,transparent_70%)]" />
      <div className="pointer-events-none absolute -bottom-[30%] -left-[5%] h-[400px] w-[400px] rounded-full bg-[radial-gradient(circle,rgba(129,140,248,0.04)_0%,transparent_70%)]" />

      <div className="content-container relative z-10 py-10 md:py-20">
        <div className="max-w-[680px]">
          {/* Badge */}
          <div
            className={`mb-4 inline-flex items-center gap-2 rounded-full border border-brand-light/20 bg-brand-light/5 px-4 py-1.5 md:mb-6 dark:border-brand-dark/20 dark:bg-brand-dark/8 ${mounted ? "animate-fade-up" : "opacity-0"}`}
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-light dark:bg-brand-dark" />
            <span className="text-xs font-medium text-brand-light dark:text-brand-dark">
              {t("heroBadge")}
            </span>
          </div>

          {/* Headline */}
          <h1
            className={`font-display text-3xl font-bold leading-[1.08] tracking-tight sm:text-4xl md:text-[56px] ${mounted ? "animate-fade-up-1" : "opacity-0"}`}
          >
            <span className="text-slate-900 dark:text-[#e8f0ff]">
              {t("heroTitle1")}{" "}
            </span>
            <span className="text-brand-light dark:text-brand-dark">
              {t("heroTitleAccent")}
            </span>
            <br />
            <span className="text-slate-900 dark:text-[#e8f0ff]">
              {t("heroTitle2")}
            </span>
          </h1>

          {/* Subtext */}
          <p
            className={`mt-4 line-clamp-3 max-w-[420px] text-sm leading-relaxed text-slate-600 sm:line-clamp-none sm:text-base md:mt-5 md:max-w-[500px] dark:text-slate-400 ${mounted ? "animate-fade-up-2" : "opacity-0"}`}
          >
            {t("heroSubtext")}
          </p>

          {/* CTAs */}
          <div
            className={`mt-6 flex flex-wrap gap-3 md:mt-8 ${mounted ? "animate-fade-up-3" : "opacity-0"}`}
          >
            <LocalizedClientLink
              href="/store"
              className="rounded-xl bg-gradient-to-r from-brand-light to-brand-600 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-light/20 transition hover:-translate-y-0.5 hover:shadow-brand-light/30 dark:from-brand-dark dark:to-brand-500 dark:shadow-brand-dark/15"
            >
              {t("heroCtaCatalog")}
            </LocalizedClientLink>
            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("kvatronix:open-ai-chat"))
                }
              }}
              className="rounded-xl border border-brand-light/25 bg-brand-light/5 px-6 py-3 text-sm font-semibold text-brand-light transition hover:bg-brand-light/10 dark:border-brand-dark/20 dark:bg-brand-dark/8 dark:text-brand-dark dark:hover:bg-brand-dark/12"
            >
              🤖 {t("heroCtaAi")}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div
          className={`mt-8 flex flex-wrap gap-6 border-t border-slate-200/60 pt-5 md:mt-14 md:gap-10 md:pt-7 dark:border-white/8 ${mounted ? "animate-fade-up-4" : "opacity-0"}`}
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-[28px] font-bold text-brand-light dark:text-brand-dark">
                {s.value}
              </div>
              <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
